
import { useState, useEffect } from "react";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormStep } from "@/models";

interface EditStepDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdateStep: (step: FormStep) => Promise<void>;
  step: FormStep | null;
  isSubmitting: boolean;
}

const EditStepDialog = ({
  isOpen,
  onClose,
  onUpdateStep,
  step,
  isSubmitting
}: EditStepDialogProps) => {
  const [stepTitle, setStepTitle] = useState("");
  const [nextStepLabel, setNextStepLabel] = useState("");

  useEffect(() => {
    if (step && isOpen) {
      console.log("EditStepDialog - Step data loaded:", step);
      setStepTitle(step.step_title || "");
      setNextStepLabel(step.next_step_label || "Next");
    }
  }, [step, isOpen]);

  const handleUpdateStep = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!step) return;
    
    console.log("EditStepDialog - Updating step:", {
      ...step,
      step_title: stepTitle,
      next_step_label: nextStepLabel || "Next"
    });
    
    await onUpdateStep({
      ...step,
      step_title: stepTitle,
      next_step_label: nextStepLabel || "Next"
    });
  };

  const handleClose = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log("EditStepDialog - Closing dialog");
    if (!isSubmitting) {
      onClose();
    }
  };

  // Prevent form submission from triggering navigation
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log("EditStepDialog - Form submission prevented");
  };

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={(open) => {
        console.log("EditStepDialog - Dialog open state changed:", open);
        if (!open && !isSubmitting) {
          handleClose();
        }
      }}
    >
      <DialogContent 
        className="sm:max-w-[425px]" 
        onPointerDownOutside={(e) => {
          // Prevent closing when submitting and stop propagation
          console.log("EditStepDialog - Pointer down outside dialog");
          if (isSubmitting) {
            e.preventDefault();
          }
        }}
        onClick={(e) => {
          // Stop clicks inside the dialog from propagating to parent elements
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <DialogHeader>
          <DialogTitle>Edit Step</DialogTitle>
          <DialogDescription>
            Update the step properties.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleFormSubmit} className="space-y-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <label htmlFor="step-title" className="text-right col-span-1 text-xs font-medium">
              Title
            </label>
            <Input
              id="step-title"
              value={stepTitle}
              onChange={(e) => setStepTitle(e.target.value)}
              className="col-span-3"
              disabled={isSubmitting}
              placeholder="Leave blank for default title"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <label htmlFor="next-step-label" className="text-right col-span-1 text-xs font-medium">
              Next label
            </label>
            <Input
              id="next-step-label"
              value={nextStepLabel}
              onChange={(e) => setNextStepLabel(e.target.value)}
              className="col-span-3"
              disabled={isSubmitting}
              placeholder="Next"
            />
          </div>
        </form>
        
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            type="button"
            disabled={isSubmitting}
            onClick={handleUpdateStep}
          >
            {isSubmitting ? "Updating..." : "Update Step"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditStepDialog;
